import _ from 'lodash';
import * as selectionSelectors from '../selection/selectionSelectors';
import type { ThunkAction } from 'types/redux';
import type {
  CompRef,
  ComponentProperties,
  DSRead,
  DSAction,
} from 'types/documentServices';

const { getSelectedCompsRefs } = selectionSelectors;

const updateFixedPosition: (
  compRef: CompRef,
  isFixedPosition: boolean,
) => ThunkAction =
  (compRef, isFixedPosition) =>
  (dispatch, getState, { editorAPI }) => {
    // eslint-disable-next-line @wix/santa-editor/deprecatedDSApi
    editorAPI.components.layout.updateRelativeToStructure(compRef, {
      fixedPosition: isFixedPosition,
    });
  };

const setPreviewState: (
  ...params: Parameters<DSAction['documentMode']['setComponentPreviewState']>
) => ThunkAction =
  (compId, previewState) =>
  (dispatch, getState, { dsActions }) => {
    dsActions.documentMode.setComponentPreviewState(compId, previewState);
  };

const updateComponentProperties: (
  ...params: Parameters<DSAction['components']['properties']['update']>
) => ThunkAction =
  (compRef, properties) =>
  (dispatch, getState, { dsActions: { components } }) =>
    components.properties.update(compRef, properties);
const updateComponentLayout: (
  ...params: Parameters<DSAction['components']['layout']['update']>
) => ThunkAction =
  (compRef, properties) =>
  (dispatch, getState, { dsActions: { components } }) =>
    components.layout.update(compRef, properties);
const updateComponentsStyle: (
  ...params: Parameters<DSAction['components']['style']['update']>
) => ThunkAction =
  (compRef, properties) =>
  (dispatch, getState, { dsActions: { components } }) =>
    components.style.update(compRef, properties);

const filterComponentsByProperty = (
  components: CompRef[],
  propKey: string,
  propertiesGetter: (
    compRef: CompRef,
  ) => Partial<ComponentProperties> | DSRead['components']['properties']['get'],
) =>
  components.filter((component) => {
    const compProperties = propertiesGetter(component);
    return _.get(compProperties, propKey);
  });

const ignoreComponentsHiddenProperty: (
  components: CompRef[],
  propertiesItem?: Partial<ComponentProperties>,
) => ThunkAction =
  (components, propertiesItem?) =>
  (dispatch, getState, { dsRead, dsActions }) => {
    const propertiesGetter = propertiesItem
      ? _.constant(propertiesItem)
      : dsRead.components.properties.get;
    const hiddenComps = filterComponentsByProperty(
      components,
      'isHidden',
      propertiesGetter,
    );
    const hiddenCompIds = hiddenComps.map((comp) => comp.id);
    dsActions.documentMode.ignoreComponentsHiddenProperty(hiddenCompIds);
  };

const ignoreComponentsCollapsedProperty: (
  components: CompRef[],
  propertiesItem?: Partial<ComponentProperties>,
) => ThunkAction =
  (components, propertiesItem) =>
  (dispatch, getState, { dsRead, dsActions }) => {
    const propertiesGetter = propertiesItem
      ? _.constant(propertiesItem)
      : dsRead.components.properties.get;
    const collapsedComps = filterComponentsByProperty(
      components,
      'isCollapsed',
      propertiesGetter,
    );

    const collapsedCompsIds = collapsedComps.map((comp) => comp.id);
    //@ts-expect-error
    dsActions.documentMode.ignoreComponentsCollapsedProperty(collapsedCompsIds); // TODO: add type to ds
  };

const onSelectedCompPropertiesChange: (
  component: CompRef,
  propertiesItem: Partial<ComponentProperties>,
) => ThunkAction = (component, propertiesItem) => (dispatch, getState) => {
  const selectedComponents = getSelectedCompsRefs(getState());

  dispatch(ignoreComponentsHiddenProperty(selectedComponents, propertiesItem));
  dispatch(
    ignoreComponentsCollapsedProperty(selectedComponents, propertiesItem),
  );
};

const onAppManifestLoaded: () => ThunkAction =
  () =>
  (dispatch, getState, { editorAPI, dsRead }) => {
    const selectedComponents = getSelectedCompsRefs(getState());
    const renderedSelectedComps = selectedComponents.filter((comp) =>
      dsRead.components.is.rendered(comp),
    );
    if (
      !_.isEmpty(renderedSelectedComps) &&
      selectedComponents.length !== renderedSelectedComps.length
    ) {
      return editorAPI.selection.selectComponentByCompRef(
        renderedSelectedComps,
      );
    }
  };

export {
  updateFixedPosition,
  setPreviewState,
  updateComponentProperties,
  updateComponentLayout,
  updateComponentsStyle,
  ignoreComponentsHiddenProperty,
  ignoreComponentsCollapsedProperty,
  onSelectedCompPropertiesChange,
  onAppManifestLoaded,
};
